import { useConfigurationBranch } from "@/hooks/useConfigurationBranch"
import { isAdminPortal } from "@/utilities/domain"
import React from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useCustomForm } from "@/hooks/useCustomForm"
import { useCrudContext } from "@/context/crudContext"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { toast } from "sonner"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
} from "@mui/material"
import { createDiscount, updateDiscount } from "@/api/discountService"
import { DevTool } from "@/components/FormDevTool"

const discountSchema = z.object({
  name: z.string().min(1, "Name is required"),
  type: z.enum(["percentage", "fixed", "fixed_price"]),
  value: z.coerce.number().min(0, "Value must be greater than 0"),
  is_active: z.boolean().default(true),
  starts_at: z.string().nullable().optional(),
  ends_at: z.string().nullable().optional(),
})

const formatDate = (date) => {
  if (!date) return ""
  return date.split(" ")[0] // fastest fix for your current format
}

export default function DiscountModel() {
  const { branchId, branchSelector } = useConfigurationBranch()
  const queryClient = useQueryClient()
  const { openDialog, setOpenDialog, selectedRow } = useCrudContext()

  const { handleSubmit, control, Controller, reset, watch } = useCustomForm({
    resolver: zodResolver(discountSchema),
  })

  React.useEffect(() => {
    if (openDialog && selectedRow) {
      reset({
        name: selectedRow.name || "",
        type: selectedRow.type || "percentage",
        value: selectedRow.value || 0,
        is_active: selectedRow.is_active ?? true,
        starts_at: formatDate(selectedRow.starts_at),
        ends_at: formatDate(selectedRow.ends_at),
      })
    } else {
      reset({
        name: "",
        type: "percentage",
        value: 0,
        is_active: true,
        starts_at: "",
        ends_at: "",
      })
    }
  }, [openDialog, selectedRow, reset])

  const createMutation = useMutation({
    mutationFn: (data) => createDiscount(data),
    onSuccess: () => {
      toast.success("Discount created successfully")
      queryClient.invalidateQueries({ queryKey: ["discount"] })
      setOpenDialog(false)
    },
    onError: (error) => {
      toast.error(error.message || "Failed to create discount")
    },
  })

  const updateMutation = useMutation({
    mutationFn: (data) => updateDiscount(selectedRow.id, data),
    onSuccess: () => {
      toast.success("Discount updated successfully")
      queryClient.invalidateQueries({ queryKey: ["discount"] })
      setOpenDialog(false)
    },
    onError: (error) => {
      toast.error(error.message || "Failed to update discount")
    },
  })

  const onSubmit = (data) => {
    if (selectedRow?.id) {
      updateMutation.mutate(data)
    } else {
      createMutation.mutate(
        isAdminPortal() ? { ...data, branch_id: branchId } : data
      )
    }
  }

  const discountType = watch("type")

  const isLoading = createMutation.isPending || updateMutation.isPending

  return (
    <Dialog
      open={openDialog}
      onClose={() => setOpenDialog(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {selectedRow?.id ? "Edit Discount" : "Create New Discount"}
      </DialogTitle>
      <DialogContent>
        {!selectedRow && branchSelector}
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <Controller
              name="name"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Discount Name"
                  fullWidth
                  variant="outlined"
                  size="small"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="type"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  select
                  label="Type"
                  fullWidth
                  variant="outlined"
                  size="small"
                  error={!!error}
                  helperText={error?.message}
                  SelectProps={{
                    native: true,
                  }}
                >
                  <option value="percentage">Percentage (%)</option>
                  <option value="fixed">Fixed Amount</option>
                  <option value="fixed_price">Fixed Price</option>
                </TextField>
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            {discountType !== "fixed_price" && (
              <Controller
                name="value"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    type="number"
                    label="Value"
                    fullWidth
                    variant="outlined"
                    size="small"
                    onFocus={(event) => {
                      event.target.select()
                    }}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            )}
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="starts_at"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Starts At"
                  fullWidth
                  variant="outlined"
                  size="small"
                  type="date"
                  error={!!error}
                  helperText={error?.message}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="ends_at"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Ends At"
                  fullWidth
                  variant="outlined"
                  size="small"
                  type="date"
                  error={!!error}
                  helperText={error?.message}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="is_active"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch {...field} checked={field.value} />}
                  label="Active"
                />
              )}
            />
          </Grid>
          <DevTool control={control} />
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          disabled={isLoading}
        >
          {isLoading
            ? "Saving..."
            : selectedRow?.id
              ? "Update Discount"
              : "Create Discount"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
